import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  saveTicketTypeTimer,
  ticketTypeTimersQuery,
  type TicketTypeTimersRow,
  type TimerCategory,
} from '../../api/settingsEaseedeskTicketTypes'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket timers.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_types/timers/index.php`: un rand pe fiecare tip, cu insigna lui
 * colorata si trei casute de minute.
 *
 * SE SALVEAZA CAND CASUTA ISI PIERDE FOCUSUL, ca in `index-1.js`, si numai
 * daca numarul chiar s-a schimbat: acolo fiecare casuta era un `<form>` de sine
 * statator si trimitea la fiecare `change`.
 */
/** Numele coloanelor, pentru casutele care n-au nicio eticheta legata de ele. */
const TIMER_LABEL: Record<TimerCategory, string> = {
  1: 'took over time',
  2: 'identify time',
  3: 'intervention time',
}

function TimerCell({
  type,
  category,
  value,
  onSaved,
}: {
  type: TicketTypeTimersRow
  category: TimerCategory
  value: number | null
  onSaved(): void
}) {
  const [text, setText] = useState(value === null ? '' : String(value))

  useEffect(() => {
    setText(value === null ? '' : String(value))
  }, [value])

  const save = useMutation({
    mutationFn: (minutes: number) => saveTicketTypeTimer(type.ID, category, minutes),
    onSuccess: () => {
      toast('Timer saved.', 'Success')
      onSaved()
    },
    onError: (error) => {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      setText(value === null ? '' : String(value))
    },
  })

  return (
    <input
      type="number"
      name="time"
      min={0}
      className="form-control"
      aria-label={type.type_name + ' - ' + TIMER_LABEL[category] + ' (minutes)'}
      title={type.type_name + ' - ' + TIMER_LABEL[category] + ' (minutes)'}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const minutes = text.trim() === '' ? 0 : Number(text)
        if (!Number.isFinite(minutes) || minutes === (value ?? 0)) return
        save.mutate(minutes)
      }}
    />
  )
}

export function TicketTypeTimersPage() {
  const queryClient = useQueryClient()
  const page = useQuery(ticketTypeTimersQuery())

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_type_timers'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed what="The ticket timers" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />

  return (
    <div>
      <SettingsHead
        strong="Ticket"
        rest="timers"
        subtitle={
          <>
            Manage, create or edit ticket <strong>timers</strong>
          </>
        }
      />

      <table className="table table-hover dataTable">
        <thead>
          <tr>
            <th width="100px"></th>
            <th className="align-left">Ticket type</th>
            <th>Took over time (minutes)</th>
            <th>Identify time (minutes)</th>
            <th>Intervention time (minutes)</th>
          </tr>
        </thead>
        <tbody>
          {page.data.types.map((type) => (
            <tr key={type.ID}>
              <td className="align-center">
                {/* `od_console.css` da fiecarui `.badge` cerneala inchisa
                    (`--od-ink-soft`) si macheta scrie doar fundalul: prescurtarea
                    se citea inchis pe cercul colorat. In portal era alba. */}
                <span
                  className="badge"
                  style={{
                    minWidth: 23,
                    backgroundColor: type.color_identifier,
                    color: '#fff',
                    textAlign: 'center',
                  }}
                >
                  {type.short_name}
                </span>
              </td>
              <td className="align-left">{type.type_name}</td>
              <td>
                <TimerCell type={type} category={1} value={type.take_over} onSaved={reload} />
              </td>
              <td>
                <TimerCell type={type} category={2} value={type.identify} onSaved={reload} />
              </td>
              <td>
                <TimerCell type={type} category={3} value={type.intervention} onSaved={reload} />
              </td>
            </tr>
          ))}
          {page.data.types.length === 0 ? (
            <tr>
              <td colSpan={5} className="color-light-grey">
                There is no ticket type on this instance.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}
